import type { MatchDiagnosticBundle } from '../diagnostics/analyzer';
import { compactDiagnosticBundle } from '../diagnostics/compact';
import { parseDiagnosticBundle } from '../replay/schema';

const DB_NAME = 'towerarena.com.diagnostics';
const DB_VERSION = 4;
const LATEST_STORE = 'latest';
const SUMMARY_STORE = 'summaries';
const FULL_STORE = 'full-bundles';
const UPLOAD_STORE = 'upload-queue';
const RECOVERY_STORE = 'recovery';
const LOCAL_LOG_LIMIT = 10;
const RETRY_DELAYS_MS = [5_000, 30_000, 5 * 60_000, 30 * 60_000, 6 * 60 * 60_000] as const;

export interface StoredDiagnosticBundle {
  matchId: string;
  createdAt: string;
  encoding: 'gzip' | 'identity';
  blob: Blob;
}

export interface DiagnosticUploadItem extends StoredDiagnosticBundle {
  sha256: string;
  bytes: number;
  attempts: number;
  nextAttemptAt: number;
  lastError: string | null;
  engineVersion: string;
  contentVersion: string;
  arenaId: string;
  critical: boolean;
  matchMode: 'bot' | 'network';
  networkMatchId: string | null;
  networkSeat: 'seat-a' | 'seat-b' | null;
  buildId: string;
  terminationReason: string;
  priority: boolean;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(LATEST_STORE)) request.result.createObjectStore(LATEST_STORE);
      if (!request.result.objectStoreNames.contains(SUMMARY_STORE)) request.result.createObjectStore(SUMMARY_STORE, { keyPath: 'matchId' });
      if (!request.result.objectStoreNames.contains(FULL_STORE)) request.result.createObjectStore(FULL_STORE, { keyPath: 'matchId' });
      if (!request.result.objectStoreNames.contains(UPLOAD_STORE)) request.result.createObjectStore(UPLOAD_STORE, { keyPath: 'matchId' });
      if (!request.result.objectStoreNames.contains(RECOVERY_STORE)) request.result.createObjectStore(RECOVERY_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Diagnostic database upgrade is blocked by another tab'));
  });
}

function transactionDone(transaction: IDBTransaction, message: string): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error(message));
    transaction.onabort = () => reject(transaction.error ?? new Error(message));
  });
}

function requestResult<T>(request: IDBRequest<T>, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error(message));
  });
}

async function sha256(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function serializeDiagnosticBundle(bundle: MatchDiagnosticBundle): Promise<StoredDiagnosticBundle> {
  const source = new Blob([JSON.stringify(compactDiagnosticBundle(bundle))], { type: 'application/json' });
  if (typeof CompressionStream === 'undefined') {
    return { matchId: bundle.manifest.matchId, createdAt: bundle.manifest.createdAt, encoding: 'identity', blob: source };
  }
  const compressed = await new Response(source.stream().pipeThrough(new CompressionStream('gzip'))).blob();
  return { matchId: bundle.manifest.matchId, createdAt: bundle.manifest.createdAt, encoding: 'gzip', blob: compressed };
}

async function deserializeBundle(stored: StoredDiagnosticBundle): Promise<MatchDiagnosticBundle> {
  let source = stored.blob;
  if (stored.encoding === 'gzip') {
    if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot decompress the saved diagnostic log');
    source = await new Response(source.stream().pipeThrough(new DecompressionStream('gzip'))).blob();
  }
  return parseDiagnosticBundle(JSON.parse(await source.text()));
}

export async function loadLatestDiagnosticBundle(): Promise<MatchDiagnosticBundle | null> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const stored = await requestResult(
      db.transaction(LATEST_STORE).objectStore(LATEST_STORE).get('latest-full') as IDBRequest<StoredDiagnosticBundle | undefined>,
      'Unable to read the latest diagnostic bundle',
    );
    return stored ? await deserializeBundle(stored) : null;
  } finally {
    db.close();
  }
}

export async function saveDiagnosticBundle(bundle: MatchDiagnosticBundle, queueForUpload = false): Promise<void> {
  const stored = await serializeDiagnosticBundle(bundle);
  const upload: DiagnosticUploadItem | null = queueForUpload ? {
    ...stored,
    sha256: await sha256(stored.blob),
    bytes: stored.blob.size,
    attempts: 0,
    nextAttemptAt: Date.now(),
    lastError: null,
    engineVersion: bundle.manifest.engineVersion,
    contentVersion: bundle.manifest.contentVersion,
    arenaId: bundle.manifest.arenaId,
    critical: bundle.summary.critical > 0,
    matchMode: bundle.manifest.matchMode,
    networkMatchId: bundle.manifest.networkMatchId,
    networkSeat: bundle.manifest.matchMode === 'network' ? bundle.replay.match.localSide === 'player' ? 'seat-a' : 'seat-b' : null,
    buildId: bundle.manifest.buildId,
    terminationReason: bundle.manifest.termination.reason,
    priority: bundle.manifest.termination.abnormal,
  } : null;
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const stores = [LATEST_STORE, SUMMARY_STORE, FULL_STORE, ...(upload ? [UPLOAD_STORE] : [])];
    const transaction = db.transaction(stores, 'readwrite');
    transaction.objectStore(LATEST_STORE).put(stored, 'latest-full');
    transaction.objectStore(FULL_STORE).put(stored);
    transaction.objectStore(SUMMARY_STORE).put({
      matchId: bundle.manifest.matchId,
      createdAt: bundle.manifest.createdAt,
      manifest: bundle.manifest,
      summary: bundle.summary,
      metrics: bundle.metrics,
    });
    if (upload) transaction.objectStore(UPLOAD_STORE).put(upload);
    await transactionDone(transaction, 'Unable to persist diagnostic bundle');

    const summaries = await requestResult(
      db.transaction(SUMMARY_STORE).objectStore(SUMMARY_STORE).getAll() as IDBRequest<Array<{ matchId: string; createdAt: string }>>,
      'Unable to read diagnostic summaries',
    );
    const obsolete = summaries.sort((left, right) => right.createdAt.localeCompare(left.createdAt)).slice(LOCAL_LOG_LIMIT);
    if (obsolete.length) {
      const pruning = db.transaction([SUMMARY_STORE, FULL_STORE], 'readwrite');
      for (const item of obsolete) {
        pruning.objectStore(SUMMARY_STORE).delete(item.matchId);
        pruning.objectStore(FULL_STORE).delete(item.matchId);
      }
      await transactionDone(pruning, 'Diagnostic pruning transaction was aborted');
    }
  } finally {
    db.close();
  }
}

export async function saveDiagnosticRecoveryBundle(bundle: MatchDiagnosticBundle): Promise<void> {
  const stored = await serializeDiagnosticBundle(bundle);
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction(RECOVERY_STORE, 'readwrite');
    transaction.objectStore(RECOVERY_STORE).put(stored, 'active-match');
    await transactionDone(transaction, 'Unable to persist diagnostic recovery checkpoint');
  } finally {
    db.close();
  }
}

export async function clearDiagnosticRecoveryBundle(matchId?: string): Promise<void> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction(RECOVERY_STORE, 'readwrite');
    const store = transaction.objectStore(RECOVERY_STORE);
    if (!matchId) store.delete('active-match');
    else {
      const request = store.get('active-match') as IDBRequest<StoredDiagnosticBundle | undefined>;
      request.onsuccess = () => { if (request.result?.matchId === matchId) store.delete('active-match'); };
    }
    await transactionDone(transaction, 'Unable to clear diagnostic recovery checkpoint');
  } finally {
    db.close();
  }
}

export async function recoverInterruptedDiagnosticBundle(queueForUpload: boolean): Promise<MatchDiagnosticBundle | null> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  let stored: StoredDiagnosticBundle | undefined;
  try {
    stored = await requestResult(
      db.transaction(RECOVERY_STORE).objectStore(RECOVERY_STORE).get('active-match') as IDBRequest<StoredDiagnosticBundle | undefined>,
      'Unable to read diagnostic recovery checkpoint',
    );
  } finally {
    db.close();
  }
  if (!stored) return null;
  const bundle = await deserializeBundle(stored);
  const recovered: MatchDiagnosticBundle = {
    ...bundle,
    manifest: {
      ...bundle.manifest,
      createdAt: new Date().toISOString(),
      termination: { ...bundle.manifest.termination, reason: 'hard-crash-or-tab-kill', abnormal: true },
    },
  };
  await saveDiagnosticBundle(recovered, queueForUpload);
  await clearDiagnosticRecoveryBundle(bundle.manifest.matchId);
  return recovered;
}

export async function loadPendingDiagnosticUploads(now = Date.now(), limit = 3): Promise<DiagnosticUploadItem[]> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const items = await requestResult(
      db.transaction(UPLOAD_STORE).objectStore(UPLOAD_STORE).getAll() as IDBRequest<DiagnosticUploadItem[]>,
      'Unable to read diagnostic upload queue',
    );
    return items
      .filter((item) => item.nextAttemptAt <= now)
      .sort((left, right) => Number(Boolean(right.priority)) - Number(Boolean(left.priority))
        || left.nextAttemptAt - right.nextAttemptAt
        || left.createdAt.localeCompare(right.createdAt))
      .slice(0, Math.max(0, limit));
  } finally {
    db.close();
  }
}

export async function markDiagnosticUploadComplete(matchId: string): Promise<void> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction(UPLOAD_STORE, 'readwrite');
    transaction.objectStore(UPLOAD_STORE).delete(matchId);
    await transactionDone(transaction, 'Unable to remove uploaded diagnostic log from queue');
  } finally {
    db.close();
  }
}

export async function markDiagnosticUploadFailed(item: DiagnosticUploadItem, error: string, now = Date.now()): Promise<void> {
  const attempts = item.attempts + 1;
  const delay = RETRY_DELAYS_MS[Math.min(attempts - 1, RETRY_DELAYS_MS.length - 1)]!;
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction(UPLOAD_STORE, 'readwrite');
    transaction.objectStore(UPLOAD_STORE).put({ ...item, attempts, nextAttemptAt: now + delay, lastError: error.slice(0, 300) });
    await transactionDone(transaction, 'Unable to update diagnostic upload retry');
  } finally {
    db.close();
  }
}

export async function clearDiagnosticUploadQueue(): Promise<void> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction(UPLOAD_STORE, 'readwrite');
    transaction.objectStore(UPLOAD_STORE).clear();
    await transactionDone(transaction, 'Unable to clear diagnostic upload queue');
  } finally {
    db.close();
  }
}

export async function clearDiagnosticHistory(): Promise<void> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction([LATEST_STORE, SUMMARY_STORE, FULL_STORE, UPLOAD_STORE, RECOVERY_STORE], 'readwrite');
    for (const store of [LATEST_STORE, SUMMARY_STORE, FULL_STORE, UPLOAD_STORE, RECOVERY_STORE]) transaction.objectStore(store).clear();
    await transactionDone(transaction, 'Diagnostic history cleanup was aborted');
  } finally {
    db.close();
  }
}

export async function deleteDiagnosticBundle(matchId: string): Promise<void> {
  const db = await openDb();
  db.onversionchange = () => db.close();
  try {
    const transaction = db.transaction([LATEST_STORE, SUMMARY_STORE, FULL_STORE, UPLOAD_STORE], 'readwrite');
    const latestStore = transaction.objectStore(LATEST_STORE);
    const latestRequest = latestStore.get('latest-full');
    transaction.objectStore(SUMMARY_STORE).delete(matchId);
    transaction.objectStore(FULL_STORE).delete(matchId);
    transaction.objectStore(UPLOAD_STORE).delete(matchId);
    latestRequest.onsuccess = () => {
      const latest = latestRequest.result as StoredDiagnosticBundle | undefined;
      if (latest?.matchId === matchId) latestStore.delete('latest-full');
    };
    await transactionDone(transaction, 'Diagnostic bundle deletion was aborted');
  } finally {
    db.close();
  }
}

export function downloadDiagnosticBundle(bundle: MatchDiagnosticBundle): void {
  const blob = new Blob([JSON.stringify(compactDiagnosticBundle(bundle))], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${bundle.manifest.matchId}.diagnostics.compact.json`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
